import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Video, Loader2, Check, Camera, Upload, Mic } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_MB = 100;

function fmtSize(bytes) {
  if (!bytes) return "";
  const mb = bytes / (1024 * 1024);
  if (mb < 1) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${mb.toFixed(1)} MB`;
}

/**
 * Customer-facing "🎥 Show us the fault" card with two upload slots:
 *   1. Fault clip — rear camera (capture="environment") pointed AT the fault
 *   2. Narration clip — front camera (capture="user") of the customer
 *      explaining it in their own words
 * Both are optional. Advisors watch the narration first for context, then
 * the fault clip for the physical evidence.
 */
export default function CustomerVideoUpload({ token, customerFirstName }) {
  const [videos, setVideos] = useState([]);
  const [uploadingRole, setUploadingRole] = useState(null); // 'fault' | 'narration' | null
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/public/jobs/${token}/customer-videos`);
      setVideos(r.data.videos || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleFile = async (file, role) => {
    if (!file) return;
    const sizeMb = file.size / (1024 * 1024);
    if (sizeMb > MAX_MB) {
      toast.error(`Please keep the video under ${MAX_MB} MB (this one is ${sizeMb.toFixed(0)} MB). Try recording a shorter clip.`);
      return;
    }
    if (!(file.type || "").startsWith("video/") && !/\.(mp4|mov|m4v|webm|3gp)$/i.test(file.name || "")) {
      toast.error("That doesn't look like a video file — please pick an MP4 or MOV.");
      return;
    }
    setUploadingRole(role);
    setProgress(0);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("role", role);
    try {
      await axios.post(`${API}/public/jobs/${token}/customer-video`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      toast.success(role === "narration"
        ? "Thank you! Your explanation is with our technician."
        : "Thank you! Your video is with our technician.");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed — please try again");
    } finally {
      setUploadingRole(null);
      setProgress(0);
    }
  };

  const faultVideos = videos.filter((v) => (v.role || "fault") === "fault");
  const narrationVideos = videos.filter((v) => v.role === "narration");
  const isUploading = !!uploadingRole;

  return (
    <div className="card-default p-5" data-testid="customer-video-upload">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-full grid place-items-center bg-primary/15 text-primary shrink-0">
          <Video className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="label-overline text-primary">Optional</div>
          <div className="font-display font-bold text-lg mt-0.5">
            Show us the fault{customerFirstName ? `, ${customerFirstName}` : ""}
          </div>
          <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
            A short video helps our technician fix it right first time. You can
            send a clip of the fault, a selfie explaining it, or both.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="text-xs text-muted-foreground py-4 text-center">
          <Loader2 className="w-3 h-3 inline animate-spin mr-1" /> Loading…
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          <UploadSlot
            role="fault"
            title="Fault clip"
            subtitle="Point your phone at the noise, leak or warning light."
            icon={<Camera className="w-4 h-4" />}
            capture="environment"
            sent={faultVideos}
            uploading={uploadingRole === "fault"}
            progress={progress}
            disabled={isUploading}
            onFile={(f) => handleFile(f, "fault")}
          />
          <UploadSlot
            role="narration"
            title="Explain it in your own words"
            subtitle='Record a selfie: "It happens when I go over 40 mph…"'
            icon={<Mic className="w-4 h-4" />}
            capture="user"
            sent={narrationVideos}
            uploading={uploadingRole === "narration"}
            progress={progress}
            disabled={isUploading}
            onFile={(f) => handleFile(f, "narration")}
          />
        </div>
      )}
      <p className="text-[11px] text-muted-foreground mt-3 text-center leading-snug">
        Max {MAX_MB} MB per clip · MP4 / MOV · around 60 seconds each
      </p>
    </div>
  );
}

function UploadSlot({ role, title, subtitle, icon, capture, sent, uploading, progress, disabled, onFile }) {
  const hasSent = sent.length > 0;
  return (
    <div
      className={`p-4 rounded-lg border ${hasSent ? "border-success/30 bg-success/5" : "border-border bg-muted/10"}`}
      data-testid={`customer-video-slot-${role}`}
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="text-primary">{icon}</span>
        <span className="font-semibold text-sm">{title}</span>
      </div>
      <p className="text-xs text-muted-foreground leading-snug mb-3">{subtitle}</p>

      {hasSent && (
        <ul className="mb-3 space-y-1" data-testid={`customer-video-list-${role}`}>
          {sent.map((v) => (
            <li
              key={v.id}
              className="flex items-center gap-1.5 text-xs px-2 py-1.5 rounded-md border border-success/30 bg-success/10"
              data-testid={`customer-video-item-${v.id}`}
            >
              <Check className="w-3 h-3 text-success shrink-0" />
              <span className="font-semibold">Video sent</span>
              <span className="ml-auto text-muted-foreground font-mono-plex">
                {fmtSize(v.size_bytes)}
              </span>
            </li>
          ))}
        </ul>
      )}

      {uploading ? (
        <div className="p-3 rounded-md border border-primary/40 bg-primary/5" data-testid={`customer-video-uploading-${role}`}>
          <div className="flex items-center gap-2 text-xs mb-2">
            <Loader2 className="w-3 h-3 animate-spin text-primary" />
            <span className="font-semibold">Uploading…</span>
            <span className="ml-auto font-mono-plex">{progress}%</span>
          </div>
          <div className="h-1 rounded-full bg-muted overflow-hidden">
            <div className="h-full bg-primary transition-all duration-200" style={{ width: `${progress}%` }} />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-1.5">
          <label
            className={`btn-primary text-xs cursor-pointer justify-center ${disabled ? "opacity-50 pointer-events-none" : ""}`}
            data-testid={`customer-video-record-btn-${role}`}
          >
            <Camera className="w-3.5 h-3.5" />
            {hasSent ? "Send another" : "Record"}
            <input type="file" accept="video/*" capture={capture} className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
          </label>
          <label
            className={`btn-outline text-xs cursor-pointer justify-center ${disabled ? "opacity-50 pointer-events-none" : ""}`}
            data-testid={`customer-video-upload-btn-${role}`}
          >
            <Upload className="w-3.5 h-3.5" />
            Gallery
            <input type="file" accept="video/*" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
          </label>
        </div>
      )}
    </div>
  );
}
